/**
 * Collection Infinite Scroll
 * Uses Section Render API to load next pages dynamically
 * 
 * Features:
 * - Automatic loading on scroll with Intersection Observer
 * - Section Render API for efficient page loading
 * - Support for mid-collection banners
 * - Manual load more button (optional)
 * - Performance optimized with requestAnimationFrame
 * - Filter/sort compatibility
 * 
 * @class CollectionInfiniteScroll
 */
class CollectionInfiniteScroll {
  constructor(container) {
    this.container = container;
    this.section = container.closest('[data-section-type="collection"]');
    this.productGrid = container.querySelector('#CollectionLoop');
    this.loadMoreTrigger = container.querySelector('[data-infinite-scroll-trigger]');
    this.loadingIndicator = container.querySelector('[data-infinite-scroll-loading]');
    
    if (!this.section || !this.productGrid) {
      console.warn('Collection infinite scroll: Required elements not found');
      return;
    }

    this.sectionId = this.section.dataset.sectionId;
    this.collectionUrl = this.section.dataset.collection;
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = this.loadMoreTrigger ? true : false;

    if (this.hasMorePages) {
      this.init();
    }
  }

  init() {
    this.setupIntersectionObserver();
    this.bindEvents();
    this.setupFilterSortListeners();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '300px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
          this.loadNextPage();
        }
      });
    }, options);

    if (this.loadMoreTrigger) {
      this.observer.observe(this.loadMoreTrigger);
    }
  }

  bindEvents() {
    // Manual load more button as fallback
    const loadMoreBtn = this.container.querySelector('[data-load-more-btn]');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (!this.isLoading && this.hasMorePages) {
          this.loadNextPage();
        }
      });
    }
  }

  setupFilterSortListeners() {
    // Listen for collection filter/sort changes
    // When filters or sorting changes, the page will reload
    // This ensures infinite scroll state is reset
    
    const filterForms = this.section.querySelectorAll('.collection__nav form, .collection-tags form');
    filterForms.forEach(form => {
      form.addEventListener('change', () => {
        // Reset infinite scroll state before form submission
        this.reset();
      });
    });

    // Listen for tag filter changes
    const tagLinks = this.section.querySelectorAll('.collection-tags a, .collection-tags-grouped a');
    tagLinks.forEach(link => {
      link.addEventListener('click', () => {
        // Reset infinite scroll state
        this.reset();
      });
    });
  }

  reset() {
    this.currentPage = 1;
    this.isLoading = false;
    
    if (this.observer && this.loadMoreTrigger) {
      this.observer.unobserve(this.loadMoreTrigger);
    }
  }

  async loadNextPage() {
    if (this.isLoading || !this.hasMorePages) return;

    this.isLoading = true;
    this.showLoading();

    try {
      const nextPage = this.currentPage + 1;
      const url = this.buildSectionUrl(nextPage);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      this.processResponse(html, nextPage);
      
    } catch (error) {
      console.error('Error loading next page:', error);
      this.handleError(error);
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  buildSectionUrl(page) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    
    params.set('page', page);
    params.set('section_id', this.sectionId);
    
    return `${this.collectionUrl}?${params.toString()}`;
  }

  processResponse(html, page) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Extract all children from CollectionLoop (products and mid-banners)
    const newItems = tempDiv.querySelectorAll('#CollectionLoop > *');
    
    if (newItems.length === 0) {
      this.hasMorePages = false;
      this.removeTrigger();
      return;
    }

    // Track products for event dispatching
    const newProducts = [];
    const fragment = document.createDocumentFragment();

    // Append new items (products and banners) to fragment first for better performance
    newItems.forEach(item => {
      // Clone the item to preserve all attributes and event listeners
      const clonedItem = item.cloneNode(true);
      
      // Add fade-in animation class for products
      if (clonedItem.classList.contains('product-item')) {
        clonedItem.classList.add('infinite-scroll-loaded');
        newProducts.push(clonedItem);
      }
      
      fragment.appendChild(clonedItem);
    });

    // Use requestAnimationFrame for smoother DOM insertion
    requestAnimationFrame(() => {
      this.productGrid.appendChild(fragment);
    });

    // Update current page
    this.currentPage = page;

    // Check if there are more pages
    const nextPageTrigger = tempDiv.querySelector('[data-infinite-scroll-trigger]');
    if (!nextPageTrigger) {
      this.hasMorePages = false;
      this.removeTrigger();
    } else {
      // Update the next page URL in trigger
      const nextPageUrl = nextPageTrigger.dataset.nextPage;
      if (this.loadMoreTrigger) {
        this.loadMoreTrigger.dataset.nextPage = nextPageUrl;
      }
    }

    // Update URL without reload
    this.updateUrl(page);

    // Trigger custom event for other scripts
    if (newProducts.length > 0) {
      this.dispatchLoadEvent(newProducts);
      
      // Reinitialize any product-specific scripts
      this.reinitializeProducts(newProducts);
    }
  }

  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('hidden');
      this.loadingIndicator.setAttribute('aria-hidden', 'false');
    }
  }

  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('hidden');
      this.loadingIndicator.setAttribute('aria-hidden', 'true');
    }
  }

  updateUrl(page) {
    const url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set('page', page);
    } else {
      url.searchParams.delete('page');
    }
    window.history.replaceState({ page }, '', url);
  }

  removeTrigger() {
    if (this.loadMoreTrigger) {
      this.observer.unobserve(this.loadMoreTrigger);
      this.loadMoreTrigger.remove();
    }
  }

  handleError(error) {
    // Show error message to user
    const errorMessage = document.createElement('div');
    errorMessage.className = 'collection-load-error';
    errorMessage.textContent = 'Unable to load more products. Please try again.';
    
    if (this.loadMoreTrigger) {
      this.loadMoreTrigger.parentNode.insertBefore(errorMessage, this.loadMoreTrigger);
    }

    // Remove error message after 5 seconds
    setTimeout(() => {
      errorMessage.remove();
    }, 5000);
  }

  dispatchLoadEvent(products) {
    const event = new CustomEvent('collection:productsLoaded', {
      detail: {
        products: products,
        page: this.currentPage
      },
      bubbles: true
    });
    this.container.dispatchEvent(event);
  }

  reinitializeProducts(products) {
    // Reinitialize product hover effects, quick view, etc.
    products.forEach(product => {
      // Lazy load images
      const lazyImages = product.querySelectorAll('.lazyload');
      if (window.lazySizes) {
        lazyImages.forEach(img => {
          window.lazySizes.loader.unveil(img);
        });
      }

      // Reinitialize AOS animations
      if (window.AOS) {
        window.AOS.init();
      }

      // Trigger product grid item initialization if exists
      const productItemEvent = new CustomEvent('product:initialized', {
        detail: { element: product },
        bubbles: true
      });
      product.dispatchEvent(productItemEvent);
    });
  }

  destroy() {
    if (this.observer && this.loadMoreTrigger) {
      this.observer.unobserve(this.loadMoreTrigger);
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const collectionContainers = document.querySelectorAll('[data-infinite-scroll-container]');
  
  collectionContainers.forEach(container => {
    new CollectionInfiniteScroll(container);
  });
});

// Expose for theme editor
if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('[data-infinite-scroll-container]');
    if (container) {
      new CollectionInfiniteScroll(container);
    }
  });
}

